/**
 * Tiny fetch wrapper for the SME Review API. All requests include credentials so
 * the HttpOnly session cookie is sent. Throws ApiError on non-2xx.
 */
export class ApiError extends Error {
  status: number;
  body: unknown;
  constructor(status: number, body: unknown, message: string) {
    super(message);
    this.status = status;
    this.body = body;
  }
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(path, {
    method,
    credentials: 'include',
    headers: body !== undefined ? { 'Content-Type': 'application/json' } : undefined,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  const text = await res.text();
  let parsed: unknown = undefined;
  if (text) {
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = text;
    }
  }
  if (!res.ok) {
    const detail =
      parsed && typeof parsed === 'object' && 'error' in parsed
        ? String((parsed as Record<string, unknown>).error)
        : `HTTP ${res.status}`;
    throw new ApiError(res.status, parsed, detail);
  }
  return parsed as T;
}

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body),
  patch: <T>(path: string, body?: unknown) => request<T>('PATCH', path, body),
};

// ---- shared types (mirror server JSON shapes) ----------------------------

// Phase 4.1 merge: review no longer owns SessionResp — the shared battle
// session is read by the root router in main.tsx. Review's api.ts only
// declares its data-shape types now.

// M9: ClusterSummary now exposes full per-lifecycle counts for BOTH study and
// battle, so the client can render the rail count line + drive filters without
// re-fetching. `total` continues to mean "all rows regardless of lifecycle".
export interface ClusterSummary {
  cluster_id: string;
  total: number;
  approved: number;
  pending: number;
  rejected: number;
  synth: number;
  draft: number;
  active: number;
  retired: number;
  battle_total: number;
  battle_approved: number;
  battle_draft: number;
  battle_active: number;
  battle_retired: number;
}

export interface StudyBody {
  examiner_definition?: string;
  key_distinctions?: string[];
  how_tested?: string;
  worked_examples?: string[];
  applications?: { agent: string; example: string }[];
  exam_link?: string;
  [k: string]: unknown;
}

// ---- seed_body typed shape (mirrors @econquest/shared seedBodySchema) -----
// Editable: stem, mark_scheme.indicative_content, model_answer, distractors, examiner_traps.
// Read-only/canonical: mark_scheme.levels bands, marks, tier, portal_key, source, ao weights,
// rubric_injected_levels.
export interface MarkPoint {
  marks: number;
  criterion: string;
}
export interface MarkLevel {
  level: number;
  marks: string; // range like "5-6"
  descriptor: string;
}
export interface MarkScheme {
  type: 'points' | 'levels';
  points?: MarkPoint[];
  levels?: MarkLevel[];
  indicative_content?: string[];
}
export interface SeedDistractor {
  option: string; // "A".."D"
  text: string;
  is_correct: boolean;
  why_wrong?: string;
}
export interface SeedBody {
  stem: string;
  command_word: string;
  marks: number;
  mark_scheme: MarkScheme;
  model_answer?: string;
  distractors?: SeedDistractor[];
  source?: { doc: string; section: string; line_start?: number };
  source_quality?: 'GENUINE' | 'SYNTHETIC';
  rubric_injected_levels?: number[];
  portal_key?: string;
  tier?: 'path6' | 'conditional' | 'final';
  [k: string]: unknown;
}

export interface Template {
  id: string;
  cluster_id: string;
  battle_type: string;
  mark_value: number;
  command_word: string | null;
  sub_concept: string | null;
  concept_tags: string[];
  boundary_tier: string | null;
  difficulty: string | null;
  examiner_traps: string[];
  seed_body: SeedBody | null;
  paper: number | null;
  status: string;
  review_status: string;
  reviewed_by: string | null;
  reviewed_at: string | null;
  sme_notes: string | null;
  edited_by_sme: boolean;
}

export interface Concept {
  id: string;
  cluster_id: string;
  concept_code: string;
  concept_title: string;
  spec_requirement: string | null;
  core_definition: string | null;
  body: StudyBody;
  registry_term: string | null;
  portal_links: string[];
  source_quality: string;
  status: string;
  review_status: string;
  reviewed_by: string | null;
  reviewed_at: string | null;
  sme_notes: string | null;
  edited_by_sme: boolean;
  created_at: string;
  updated_at: string;
  templates: Template[];
}

// ---- portal view --------------------------------------------------------
// M9: per-lifecycle counts exposed for the rail count line + filter UI.
// Server `pending`/`rejected`/`draft`/`live`/`retired` are nullable-tolerant
// so old client builds talking to a new server (or vice versa during a rolling
// deploy) don't crash on missing fields.
export interface PortalGroup {
  portal_key: string;
  tier: string | null;
  marks: number | null;
  command_word: string | null;
  total: number;
  approved: number;
  pending?: number;
  rejected?: number;
  synth: number;
  draft?: number;
  live?: number;
  retired?: number;
}

export interface PortalDetail {
  portal_key: string;
  templates: Template[];
}

// ---- AI helper ----------------------------------------------------------
export type SuggestableField =
  | 'stem'
  | 'indicative_content'
  | 'model_answer'
  | 'distractors'
  | 'examiner_traps';

export interface AiSuggestion {
  field: SuggestableField;
  current: unknown;
  suggested: unknown;
  rationale: string;
}

export interface AiSuggestResponse {
  template_id: string;
  suggestions: AiSuggestion[];
  providerUsed: string;
}

export interface SelfGradeResult {
  template_id: string;
  score: number;
  max: number;
  verdict: 'full' | 'short';
  feedback: string;
  breakdown: { ao1?: number; ao2?: number; ao3?: number; ao4?: number };
  providerUsed: string;
}

// ---- M10g: AI new-question authoring ------------------------------------
// The generated draft mirrors the seed_body shape (points-based mark scheme).
export interface GeneratedQuestion {
  stem: string;
  command_word: string;
  marks: number;
  mark_scheme: {
    type: 'points';
    points: { marks: number; criterion: string }[];
    indicative_content?: string[];
  };
  model_answer?: string;
  distractors?: { option: string; text: string; is_correct: boolean; why_wrong?: string }[];
  examiner_traps: string[];
}

export interface NewQuestionResponse {
  cluster_id: string;
  concept_code: string;
  question: GeneratedQuestion;
  providerUsed: string;
}

// ---- M11: AI new-PORTAL-question authoring -------------------------------
// The generated draft is the question-specific substance only; the levels mark
// scheme is injected server-side at persist time (POST /api/portal-template).
export type PortalTier = 'path6' | 'conditional' | 'final';

export interface GeneratedPortalQuestion {
  stem: string;
  indicative_content: string[];
  examiner_traps: string[];
  model_answer?: string;
  concept_tags: string[];
}

export interface NewPortalQuestionResponse {
  portal_key: string;
  tier: PortalTier;
  mark_value: number;
  command_word: string;
  question: GeneratedPortalQuestion;
  providerUsed: string;
}

/** POST /api/ai/new-portal-question — LLM preview, NO DB write. */
export function generatePortalQuestion(input: {
  portal_key: string;
  tier: PortalTier;
  command_word?: string;
}): Promise<NewPortalQuestionResponse> {
  return api.post<NewPortalQuestionResponse>('/api/ai/new-portal-question', input);
}

/** POST /api/portal-template — validate + INSERT the previewed draft as DRAFT/pending. */
export function savePortalQuestion(input: {
  portal_key: string;
  tier: PortalTier;
  command_word?: string;
  question: GeneratedPortalQuestion;
}): Promise<Template> {
  return api.post<Template>('/api/portal-template', input);
}

export interface PlanRow {
  table: 'study_content' | 'question_templates';
  id: string;
  label: string;
  fromStatus: string;
  toStatus: string;
}

export interface SkippedRow {
  table: 'study_content' | 'question_templates';
  id: string;
  label: string;
  reason: string;
}

export interface PromoteResult {
  action: 'promote' | 'retire';
  target: 'study' | 'battle' | 'both';
  dryRun: boolean;
  plan: PlanRow[];
  studyChanged: number;
  battleChanged: number;
  studyNoop: number;
  battleNoop: number;
  studySkipped: number;
  battleSkipped: number;
  skippedUnapproved: SkippedRow[];
}
